import { Children, type ReactNode, isValidElement, useState, type ReactElement, cloneElement } from 'react'
import { FormGroup as MuiFormGroup, type FormGroupProps as MuiFormGroupProps, FormHelperText } from '@mui/material'
import { ExpandLessIcon, ExpandMoreIcon } from '@dhl-ui/icons'
import { hasOwnProperty } from '../../helpers'
import { type CheckboxProps } from '../../inputs/Checkbox'
import { type RadioGroupProps } from '../../inputs/RadioGroup'
import { type SwitchProps } from '../../inputs/Switch'
import { Box } from '../../primitives/Box'
import { Button } from '../../primitives/Button'
import { type DataTestIdAttribute, type PropsWithSpecificChildren } from '../../types'
import { Body1, Caption1 } from '../../primitives/Typography'

// reason: better
// eslint-disable-next-line @typescript-eslint/naming-convention
const SHOW_MORE_THRESHOLD = 7
// reason: better
// eslint-disable-next-line @typescript-eslint/naming-convention
const SHOW_AS_ROW_THRESHOLD = 4

type ChildProps = { disabled?: boolean } & (CheckboxProps | SwitchProps | RadioGroupProps<number | string>)

export type FormGroupProps = {
  /**
   * set the helper text to show underneath the FormGroup
   */
  helperText?: ReactNode
  /**
   * set the label to show above the FormGroup
   */
  label?: ReactNode
  /**
   * indicate whether an error occurred
   * @default false
   */
  error?: boolean
  /**
   * disable the entire group, propagated to children
   * @default false
   */
  disabled?: boolean
  /**
   * components to show in the body, can be Checkbox, RadioGroup or Switch
   */
  children: PropsWithSpecificChildren<ChildProps>
  /**
   * mark whether - if applicable - the default behaviour should expand the list
   * @default false
   */
  defaultExpanded?: boolean
  /**
   * threshold until the 'show more' will be displayed
   * @default SHOW_MORE_THRESHOLD
   */
  showMoreThreshold?: number
  /**
   * text for the expand text
   * @default 'show more'
   */
  showMoreLabel?: string
  /**
   * text for the expand text
   * @default 'show less'
   */
  showLessLabel?: string
} & Pick<MuiFormGroupProps, 'row'> &
  DataTestIdAttribute

/**
 * this is problematic; we normally have the component within
 * by moving the prop to this, we need to decorate
 disabled?: boolean
 * */

/**
 * FormGroup
 *
 * Demos:
 * - [FormGroup](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/constructs-formgroup--docs)
 *
 */
export const FormGroup = ({
  helperText,
  label,
  row = false,
  disabled = false,
  error = false,
  showMoreThreshold = SHOW_MORE_THRESHOLD,
  defaultExpanded = false,
  showMoreLabel = 'show more',
  showLessLabel = 'show less',
  children,
  'data-testid': dataTestId = 'form-group',
}: FormGroupProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  const toggle = () => setExpanded((isExpanded) => !isExpanded)

  const childrenArray: ReactElement<ChildProps>[] = Children.toArray(children).filter(isValidElement<ChildProps>)

  const showAsRow = row && childrenArray.length < SHOW_AS_ROW_THRESHOLD
  const withExpandButton = !showAsRow && childrenArray.length > showMoreThreshold

  const activeChildren = expanded || showAsRow ? childrenArray : [...childrenArray].slice(0, showMoreThreshold)

  // reason: still readable
  // eslint-disable-next-line no-nested-ternary
  const variant = disabled ? 'inherit' : error ? 'error' : 'primary'

  const childIsRadioGroup = (child: ReactElement) =>
    hasOwnProperty(child, 'type') && hasOwnProperty(child.type, 'name') && child.type.name === 'RadioGroup'

  const firstChildIsRadioGroup = childrenArray[0] ? childIsRadioGroup(childrenArray[0]) : false

  /**
   * RadioGroup also renders a MuiFormGroup...
   */

  return (
    <Box sx={disabled ? { color: 'action.disabled' } : {}}>
      {label && (
        <Body1 variant={variant} data-testid={`${dataTestId}-label`}>
          {label}
        </Body1>
      )}
      {firstChildIsRadioGroup ? (
        activeChildren.map((child) => cloneElement(child, { disabled, row }))
      ) : (
        <MuiFormGroup row={showAsRow} data-testid={dataTestId}>
          {activeChildren.map((child) => cloneElement(child, { disabled }))}
        </MuiFormGroup>
      )}
      {withExpandButton && (
        <Button
          data-testid={`${dataTestId}-expand`}
          variant="text"
          dense
          onClick={toggle}
          align="start"
          disabled={disabled}
          endIcon={
            expanded ? (
              <ExpandLessIcon data-testid={`${dataTestId}-expand-less`} />
            ) : (
              <ExpandMoreIcon data-testid={`${dataTestId}-expand-more`} />
            )
          }
          sx={{ my: 1 }}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </Button>
      )}
      {helperText && (
        <FormHelperText
          component={Caption1}
          sx={{ mt: 0, display: 'block' }}
          error={error && !disabled}
          disabled={disabled}
          data-testid={`${dataTestId}-helper-text`}
        >
          {helperText}
        </FormHelperText>
      )}
    </Box>
  )
}
